import React, { useCallback, useMemo, useRef } from 'react';
import {
	FAILED_FIELD_STATE,
	PENDING_FIELD_STATE,
	STORED_FIELD_STATE,
} from '@atlassian/jira-forge-controllers-inline-fields-state/src/constants.tsx';
import { useInlineField } from '@atlassian/jira-forge-controllers-inline-fields-state/src/index.tsx';
import { fireOperationalEditFailedEvent } from '@atlassian/jira-forge-ui-analytics/src/services/custom-field-edit/index.tsx';
import {
	CONTEXT_TOKEN_TYPE_JIRA,
	CUSTOM_FIELD_MODULE,
	ENTRY_POINT_EDIT,
} from '@atlassian/jira-forge-ui-constants/src/constants.tsx';
import type { CustomUIBridge } from '@atlassian/jira-forge-ui-types/src/common/types/bridge.tsx';
import type { ForgeCustomFieldValue } from '@atlassian/jira-forge-ui-types/src/common/types/contexts/custom-field.tsx';
import type { LazyCustomFieldInlineEditFormProps } from '@atlassian/jira-forge-ui-types/src/common/types/custom-field.tsx';
import {
	isCustomUIExtension,
	isNativeUiExtension,
} from '@atlassian/jira-forge-ui-utils-internal/src/utils/extension/index.tsx';
import Placeholder from '@atlassian/jira-placeholder/src/index.tsx';
import { fg } from '@atlassian/jira-feature-gating';
import { Skeleton } from '../../../../../common/ui/skeleton/main.tsx';
import { StopSubmitPropagation } from '../../../../../common/ui/submit-stop-propogation-wrapper/index.tsx';
import {
	LazyCustomFieldInlineEditExtension,
	LazyCustomFieldInlineEditRenderer,
} from '../../main.tsx';

export type Bridge = {
	submit: (opts: { data: ForgeCustomFieldValue }) => Promise<void>;
};
const skeletonConfig = [{ width: 100 }];

export const LazyCustomFieldInlineEditForm = ({
	onSubmit,
	onModalRender,
	...restProps
}: LazyCustomFieldInlineEditFormProps) => {
	const requestsCount = useRef(0);
	const [, { setFieldState }] = useInlineField(restProps.extensionData.fieldId);

	const { extension, extensionData, extensionPayload } = restProps;

	const loadingComponent = useMemo(() => <Skeleton skeletonConfig={skeletonConfig} />, []);
	const extensionDataValue = useMemo(() => {
		const preparedExtensionData =
			extensionData?.issue && extensionData?.project
				? extensionData
				: {
						fieldId: extensionData.fieldId,
						fieldType: extensionData.fieldType,
						fieldName: extensionData.fieldName,
						renderContext: extensionData.renderContext,
						...(fg('ditto-fcf-support-new-manifest-on-frontend') && {
							experience: extensionData.experience,
						}),
					};

		return {
			...preparedExtensionData,
			type: extension.type,
			entryPoint: ENTRY_POINT_EDIT,
			isInline: true,
		};
	}, [extension, extensionData]);

	const extraProps = useMemo(
		() => ({
			loadingComponent,
			module: CUSTOM_FIELD_MODULE,
			entryPoint: ENTRY_POINT_EDIT,
			extensionData: extensionDataValue,
			extensionPayload,
			contextToken: CONTEXT_TOKEN_TYPE_JIRA,
		}),
		[extensionDataValue, extensionPayload, loadingComponent],
	);

	const createRetryFunction = useCallback(
		// eslint-disable-next-line @typescript-eslint/no-explicit-any
		(fieldValue: Promise<Record<any, any>>) => async () => {
			try {
				setFieldState({
					fieldId: extensionData.fieldId,
					status: PENDING_FIELD_STATE,
				});
				requestsCount.current += 1;
				const submitValue = await Promise.resolve(fieldValue);
				onSubmit(submitValue);
				requestsCount.current -= 1;

				if (requestsCount.current === 0) {
					setFieldState({
						fieldId: extensionData.fieldId,
						status: STORED_FIELD_STATE,
					});
				}
				// eslint-disable-next-line @typescript-eslint/no-explicit-any
			} catch (error: any) {
				setFieldState({
					fieldId: extensionData.fieldId,
					status: FAILED_FIELD_STATE,
				});
				fireOperationalEditFailedEvent(extensionData.renderContext, {
					error,
					isInline: true,
				});
			}

			return undefined;
		},
		[onSubmit, extensionData, setFieldState],
	);

	const submit = useCallback(
		// eslint-disable-next-line @typescript-eslint/no-explicit-any
		async ({ data: fieldValue }: { data: Promise<Record<any, any>> }): Promise<undefined> => {
			const retryFunction = createRetryFunction(fieldValue);
			setFieldState({ fieldId: extensionData.fieldId, retryFunction });

			await retryFunction();
			return undefined;
		},
		[createRetryFunction, extensionData.fieldId, setFieldState],
	);

	const bridge: CustomUIBridge = useMemo(
		() => ({
			submit,
		}),
		[submit],
	);

	if (
		isNativeUiExtension(extension, ENTRY_POINT_EDIT) ||
		isCustomUIExtension(extension, ENTRY_POINT_EDIT)
	) {
		return (
			<StopSubmitPropagation>
				<Placeholder name="custom-field-edit" fallback={null}>
					<LazyCustomFieldInlineEditExtension
						{...restProps}
						{...extraProps}
						onModalRender={onModalRender}
						onSubmit={onSubmit}
						bridge={bridge}
					/>
				</Placeholder>
			</StopSubmitPropagation>
		);
	}

	return (
		<StopSubmitPropagation>
			<Placeholder name="custom-field-inline-edit" fallback={loadingComponent}>
				<LazyCustomFieldInlineEditRenderer
					{...restProps}
					{...extraProps}
					onModalRender={onModalRender}
					onSubmit={onSubmit}
					bridge={bridge}
				/>
			</Placeholder>
		</StopSubmitPropagation>
	);
};
